import { Channel, ChannelMemberResponse, EventTypes } from "stream-chat";

type CachedMembers = {
  members: Promise<ChannelMemberResponse[]>;
  unsubscribe: () => void;
};

// Stream preloads at most this many members and pages queryMembers by it
const MEMBER_PAGE_SIZE = 100;
const MAX_CACHED_CHANNELS = 20;
const MEMBERSHIP_CHANGE_EVENTS: EventTypes[] = [
  "member.added",
  "member.removed",
];

const membersCache = new Map<string, CachedMembers>();

// Pages from 0: the preloaded members aren't guaranteed to be the first page
const queryAllPages = async (
  channel: Channel
): Promise<ChannelMemberResponse[]> => {
  const collected: ChannelMemberResponse[] = [];
  let offset = 0;

  while (true) {
    const { members } = await channel.queryMembers(
      {},
      {},
      { limit: MEMBER_PAGE_SIZE, offset }
    );
    collected.push(...members);
    if (members.length < MEMBER_PAGE_SIZE) return collected;
    offset += MEMBER_PAGE_SIZE;
  }
};

const removeFromCache = (cid: string) => {
  membersCache.get(cid)?.unsubscribe();
  membersCache.delete(cid);
};

const addToCache = (cid: string, entry: CachedMembers) => {
  removeFromCache(cid);
  membersCache.set(cid, entry);

  if (membersCache.size > MAX_CACHED_CHANNELS) {
    const [oldestCid] = membersCache.keys();
    removeFromCache(oldestCid);
  }
};

// Stream doesn't keep channel.data.member_count current, so listen instead
const subscribeToMembershipChanges = (channel: Channel): (() => void) => {
  const subscriptions = MEMBERSHIP_CHANGE_EVENTS.map((eventType) =>
    channel.on(eventType, () => removeFromCache(channel.cid))
  );

  return () =>
    subscriptions.forEach((subscription) => subscription.unsubscribe());
};

/**
 * Every member of the channel, fetched page by page when the loaded state
 * doesn't hold them all. Cached per channel until someone joins or leaves it.
 */
export const fetchAllChannelMembers = (
  channel: Channel
): Promise<ChannelMemberResponse[]> => {
  const initialMembers = Object.values(channel.state?.members ?? {});
  const totalMembers = channel.data?.member_count;

  // Channel lists can load a channel with only some of its members
  const hasAllMembers =
    typeof totalMembers === "number" && initialMembers.length >= totalMembers;
  if (hasAllMembers) return Promise.resolve(initialMembers);

  const cached = membersCache.get(channel.cid);
  if (cached) return cached.members;

  const members: Promise<ChannelMemberResponse[]> = queryAllPages(
    channel
  ).catch((error) => {
    console.error("Failed to load all channel members", error);
    if (membersCache.get(channel.cid)?.members === members) {
      removeFromCache(channel.cid);
    }
    return initialMembers;
  });
  addToCache(channel.cid, {
    members,
    unsubscribe: subscribeToMembershipChanges(channel),
  });

  return members;
};
